import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select } from "@/components/ui/select";
import { api } from "@/lib/api";
import { Link, Navigate, useLoaderData } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";

/**
 * `/agents/tester/learnings`: what Prokop lacked to verify tickets. Sending one
 * opens a feature for Metoděj; until then it is only a proposal.
 */

type Status = "draft" | "dismissed";

type Learning = {
  id: string;
  title: string;
  detail: string;
  evidenceCount: number;
  status: Status;
  createdAt: string;
  tickets: Array<{ workItemId: string; globalId: number | null; title: string; at: string }>;
  workItem: { id: string; globalId: number; status: string | null } | null;
};

const TABS = [
  { key: "draft", label: "Proposed" },
  { key: "dismissed", label: "Dismissed" },
] as const;

export function TesterLearningsPage() {
  const { user } = useLoaderData({ from: "/_app" });
  const [tab, setTab] = useState<Status>("draft");
  const [rows, setRows] = useState<Learning[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [projects, setProjects] = useState<Array<{ id: string; name: string; slug: string }>>([]);
  const [target, setTarget] = useState<Record<string, string>>({});

  const load = useCallback((t: Status) => {
    setRows(null);
    return api
      .get<{ learnings: Learning[] }>(`/api/tester-learnings?status=${t}`)
      .then((res) => setRows(res.learnings))
      .catch(() => setError("Couldn't load learnings."));
  }, []);

  useEffect(() => {
    if (user.globalRole !== "admin") return;
    void load(tab);
  }, [user.globalRole, tab, load]);

  useEffect(() => {
    if (user.globalRole !== "admin") return;
    api
      .get<{ projects: Array<{ id: string; name: string; slug: string }> }>(
        "/api/projects?pageSize=200",
      )
      .then((res) => setProjects(res.projects))
      .catch(() => {
        /* the picker stays empty and sending stays disabled */
      });
  }, [user.globalRole]);

  // Prokop's code is Hub's, so that is where the feature goes unless someone picks otherwise.
  const hubId = projects.find((p) => p.slug === "hub")?.id ?? "";

  async function run(id: string, call: () => Promise<unknown>, failure: string) {
    setBusyId(id);
    setError(null);
    try {
      await call();
      await load(tab);
    } catch {
      setError(failure);
    } finally {
      setBusyId(null);
    }
  }

  if (user.globalRole !== "admin") return <Navigate to="/" />;

  return (
    <div className="space-y-4 p-6">
      <div>
        <h2 className="text-xl font-semibold">What Prokop can't test yet</h2>
        <p className="text-sm text-muted-foreground">
          Each card is a capability Prokop lacked when a ticket stayed unverified, with the tickets
          it blocked. Send it to Metoděj and it becomes a feature to build; dismiss it and Prokop
          stops proposing it.
        </p>
      </div>

      <div className="flex gap-1 border-b">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            data-testid={`tester-learnings-tab-${t.key}`}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
              tab === t.key
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
      {rows && rows.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="tester-learnings-empty">
          Nothing here.
        </p>
      )}

      {rows?.map((r) => {
        const projectId = target[r.id] ?? hubId;
        return (
          <Card key={r.id} data-testid="tester-learning-card">
            <CardContent className="space-y-2 pt-4">
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <Badge variant="secondary">
                  blocked {r.evidenceCount} ticket{r.evidenceCount === 1 ? "" : "s"}
                </Badge>
                {r.workItem && <Badge>sent to Metoděj</Badge>}
              </div>
              <p className="font-medium text-sm">{r.title}</p>
              <p className="whitespace-pre-wrap text-sm text-muted-foreground">{r.detail}</p>
              <details className="text-xs text-muted-foreground">
                <summary className="cursor-pointer">Tickets</summary>
                <ul className="mt-1 space-y-0.5">
                  {r.tickets.map((t) => (
                    <li key={`${t.workItemId}-${t.at}`}>
                      {t.globalId ? (
                        <Link
                          to="/work-item/$id"
                          params={{ id: String(t.globalId) }}
                          className="underline"
                        >
                          #{t.globalId}
                        </Link>
                      ) : null}{" "}
                      {t.title}
                    </li>
                  ))}
                </ul>
              </details>
              <div className="flex flex-wrap items-center gap-2 border-t pt-2">
                {r.workItem ? (
                  <span
                    className="text-xs text-muted-foreground"
                    data-testid="tester-learning-sent"
                  >
                    Feature{" "}
                    <Link
                      to="/work-item/$id"
                      params={{ id: String(r.workItem.globalId) }}
                      className="underline"
                    >
                      #{r.workItem.globalId}
                    </Link>
                    {r.workItem.status ? ` (${r.workItem.status})` : ""}
                  </span>
                ) : (
                  <>
                    <Select
                      size="sm"
                      aria-label="Project for the feature"
                      value={projectId}
                      onChange={(v) => setTarget((prev) => ({ ...prev, [r.id]: v }))}
                      options={[
                        { value: "", label: "Pick a project" },
                        ...projects.map((p) => ({ value: p.id, label: p.name })),
                      ]}
                    />
                    <Button
                      type="button"
                      size="sm"
                      disabled={busyId === r.id || !projectId}
                      onClick={() =>
                        void run(
                          r.id,
                          () => api.post(`/api/tester-learnings/${r.id}/send`, { projectId }),
                          "Couldn't send that to Metoděj.",
                        )
                      }
                      data-testid="tester-learning-send"
                    >
                      Send to Metoděj
                    </Button>
                  </>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={busyId === r.id}
                  onClick={() =>
                    void run(
                      r.id,
                      () =>
                        api.patch(`/api/tester-learnings/${r.id}`, {
                          status: r.status === "draft" ? "dismissed" : "draft",
                        }),
                      "Couldn't save that.",
                    )
                  }
                >
                  {r.status === "draft" ? "Dismiss" : "Back to proposed"}
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
